import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["vehiclesList", "statusIndicators"]
  
  connect() {
    
    // Debounce updates para evitar atualizações excessivas
    this.debouncedUpdateVehicleData = this.debounce(this.updateVehicleDataInternal.bind(this), 500)
    
    // Retry logic para reconexão automática
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000
    
    // Array para rastrear notificações ativas
    this.activeNotifications = []
    
    this.setupWebSocket()
  }

  disconnect() {
    
    // Cleanup WebSocket subscription
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    
    // Cleanup reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    
    // Cleanup active notifications
    this.activeNotifications.forEach(notification => {
      if (notification.parentNode) {
        notification.remove()
      }
    })
    this.activeNotifications = []
    
    // Clear debounced function
    if (this.debouncedUpdateVehicleData && this.debouncedUpdateVehicleData.cancel) {
      this.debouncedUpdateVehicleData.cancel()
    }
  }

  setupWebSocket() {
    this.subscription = consumer.subscriptions.create("MonitoringChannel", {
      connected: () => {
        this.showConnectionStatus("connected")
      },

      disconnected: () => {
        this.showConnectionStatus("disconnected")
        this.attemptReconnect()
      },

      received: (data) => {
        this.handleWebSocketData(data)
      }
    })
  }

  handleWebSocketData(data) {
    switch (data.type) {
      case "initial_data":
        this.updateVehicleData(data.data)
        break
        
      case "vehicle_update":
        this.updateSingleVehicle(data.vehicle)
        this.showUpdateNotification()
        break
        
      case "manual_update":
        this.updateVehicleData(data.data)
        break
        
      case "error":
        this.showError(data.message)
        break
        
      default:
    }
  }

  updateVehicleData(data) {
    // Usar debounced version para evitar updates excessivos
    this.debouncedUpdateVehicleData(data)
  }
  
  updateVehicleDataInternal(data) {
    // Atualizar contadores de status
    this.updateStatusCounters(data.counts)
    
    // Atualizar lista de veículos (se necessário)
    // Por enquanto, apenas log - implementação completa depois
  }

  updateSingleVehicle(vehicleData) {
    
    // Encontrar o controller do mapa e atualizar o marcador
    const mapController = this.application.getControllerForElementAndIdentifier(
      document.querySelector('[data-controller*="monitoring-map"]'), 
      "monitoring-map"
    )
    
    if (mapController && mapController.updateVehicleMarker) {
      mapController.updateVehicleMarker(vehicleData)
    } else {
    }
  }

  updateStatusCounters(counts) {
    if (!this.hasStatusIndicatorsTarget) return

    // Atualizar indicadores de ignição
    this.updateCounter('[data-status="on"]:not([data-sort-type])', counts.ignition_on)
    this.updateCounter('[data-status="idle"]:not([data-sort-type])', counts.ignition_idle)
    this.updateCounter('[data-status="off"]:not([data-sort-type])', counts.ignition_off)
    
    // Atualizar indicadores de comunicação
    this.updateCounter('[data-status="ok"][data-sort-type="commStatus"]', counts.comm_ok)
    this.updateCounter('[data-status="fail"][data-sort-type="commStatus"]', counts.comm_fail)
  }

  updateCounter(selector, count) {
    const elements = document.querySelectorAll(`${selector} span`)
    elements.forEach(span => {
      if (span.textContent !== count.toString()) {
        span.textContent = count
        this.animateCounterUpdate(span)
      }
    })
  }

  animateCounterUpdate(element) {
    element.style.transform = 'scale(1.2)'
    element.style.transition = 'transform 0.2s ease'
    
    setTimeout(() => {
      element.style.transform = 'scale(1)'
    }, 200)
  }

  showConnectionStatus(status) {
    // Mostrar indicador visual de conexão
    const indicator = document.querySelector('.connection-indicator')
    if (indicator) {
      indicator.className = `connection-indicator ${status}`
      indicator.textContent = status === 'connected' ? '🟢' : '🔴'
    }
  }

  showUpdateNotification() {
    // Limitar número de notificações simultâneas
    if (this.activeNotifications.length >= 3) {
      return
    }
    
    // Mostrar notificação discreta de atualização
    const notification = document.createElement('div')
    notification.className = 'update-notification'
    notification.textContent = '🔄 Dados atualizados'
    notification.style.cssText = `
      position: fixed;
      top: ${20 + (this.activeNotifications.length * 60)}px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 8px 16px;
      border-radius: 6px;
      font-size: 14px;
      z-index: 9999;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    `
    
    document.body.appendChild(notification)
    this.activeNotifications.push(notification)
    
    // Fade in
    setTimeout(() => notification.style.opacity = '1', 100)
    
    // Fade out e remover
    setTimeout(() => {
      notification.style.opacity = '0'
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove()
        }
        // Remove from active notifications array
        const index = this.activeNotifications.indexOf(notification)
        if (index > -1) {
          this.activeNotifications.splice(index, 1)
        }
      }, 300)
    }, 2000)
  }

  showError(message) {
    // Implementar notificação de erro se necessário
  }

  debounce(func, wait) {
    let timeout
    const debounced = function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func.apply(this, args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
    
    debounced.cancel = () => {
      clearTimeout(timeout)
    }
    
    return debounced
  }
  
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.showError("Conexão perdida. Recarregue a página para tentar novamente.")
      return
    }
    
    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1) // Exponential backoff
    
    
    this.reconnectTimer = setTimeout(() => {
      this.setupWebSocket()
    }, delay)
  }
  
  // Método público para solicitar atualização manual
  requestUpdate() {
    if (this.subscription) {
      this.subscription.perform("request_update")
    } else {
      this.attemptReconnect()
    }
  }
}